// Pure locale-tag conversion — its own module so the storefront can import it
// without pulling the whole 134-entry language table into a browse-page bundle
// (same reason `apps/translate-langs.ts` is split out).
//
// An agency's site language is stored and served as a WordPress core locale
// code: `ne_NP`, `pt_BR`, `de_DE_formal` (see SUPPORTED_LANGUAGES and
// SITE_LANGUAGES). The web platform wants a BCP-47 language TAG instead, and
// the two are not interchangeable — `<html lang>`, the web app manifest's
// `lang`, and every `Intl` formatter reject the underscore form. Passing a
// stored code straight through makes `Intl.DateTimeFormat` throw, so callers
// that guard with a try/catch silently render English regardless of which
// language the operator picked.
//
// Open Graph is the one consumer that genuinely wants `language_TERRITORY`;
// traveler-web's `normalizeOgLocale` converts back for `og:locale`.

/** WordPress's own suffix on a `language_REGION` code — a formality register
 *  (`de_DE_formal`) or an orthography (`pt_PT_ao90`). None is a registered
 *  BCP-47 variant: `de-DE-formal` merely parses as one and carries no data,
 *  while `pt-PT-ao90` is not even well-formed (a variant is 5-8 alphanumerics,
 *  or 4 starting with a digit) and throws in `Intl`. Both are dropped, so the
 *  tag degrades to the real `language-REGION` instead of the fallback. */
const WP_VARIANT_SUFFIX = /^([a-z]{2,3}_[a-z]{2})_[a-z0-9]+$/i;

/**
 * Convert a stored site-language code to a BCP-47 tag `Intl` and `<html lang>`
 * accept. Idempotent — an already-valid tag is returned canonicalized.
 * Anything unusable falls back to `fallback`.
 */
export function toBcp47Locale(
  code: string | null | undefined,
  fallback = "en",
): string {
  const raw = (code ?? "").trim();
  if (!raw) return fallback;

  const candidate = raw.replace(WP_VARIANT_SUFFIX, "$1").replace(/_/g, "-");
  try {
    return Intl.getCanonicalLocales(candidate)[0] ?? fallback;
  } catch {
    return fallback;
  }
}
